import { useEffect, useRef } from 'react'

interface AutoscrollOptions {
  scrollContainer: HTMLElement | null
  isPlaying: boolean
  /** Duration in seconds for the scroll animation */
  duration: number | null
  /** Called when the user manually scrolls during autoscroll */
  onUserScroll?: () => void
}

export const useAutoscroll = ({ scrollContainer, isPlaying, duration, onUserScroll }: AutoscrollOptions) => {
  const animationRef = useRef<number>(null)
  const startTimeRef = useRef<number>(null)
  const startScrollRef = useRef<number>(0)
  const isAnimatingRef = useRef(false)
  /**
   * True for the brief window between our own rAF-driven `scrollTo` and the
   * `scroll` event it triggers. Lets the scroll listener recognise that event
   * as ours WITHOUT reading `scrollTop` — reading layout right after a scroll
   * write forces a synchronous reflow every frame on the large performance DOM
   * (GH #1544).
   */
  const isProgrammaticScrollRef = useRef(false)

  // Attach scroll listener to detect user interaction
  useEffect(() => {
    if (!scrollContainer || !isPlaying) {
      return
    }

    const handleScroll = () => {
      if (!isAnimatingRef.current) {
        return
      }
      // Our own scroll write flagged this event — consume it without touching
      // layout. A scroll event while the flag is clear is a genuine user scroll.
      if (isProgrammaticScrollRef.current) {
        isProgrammaticScrollRef.current = false
        return
      }
      onUserScroll?.()
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [scrollContainer, isPlaying, onUserScroll])

  useEffect(() => {
    if (!isPlaying || !scrollContainer || !duration) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      isAnimatingRef.current = false
      isProgrammaticScrollRef.current = false
      return
    }

    // Geometry is read once per activation, not per frame.
    const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight
    startScrollRef.current = scrollContainer.scrollTop
    startTimeRef.current = performance.now()
    isAnimatingRef.current = true

    const animate = (currentTime: number) => {
      if (!startTimeRef.current) {
        return
      }

      const elapsed = (currentTime - startTimeRef.current) / 1000 // seconds
      const progress = Math.min(elapsed / duration, 1)
      const targetScroll = startScrollRef.current + (maxScroll - startScrollRef.current) * progress

      // Flag the scroll event this write will emit as ours, so the listener
      // classifies it without re-reading scrollTop (GH #1544).
      isProgrammaticScrollRef.current = true
      scrollContainer.scrollTo({
        top: targetScroll,
        behavior: 'auto',
      })

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        isAnimatingRef.current = false
        isProgrammaticScrollRef.current = false
      }
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (!animationRef.current) {
        return
      }
      cancelAnimationFrame(animationRef.current)
      isAnimatingRef.current = false
      isProgrammaticScrollRef.current = false
    }
  }, [isPlaying, scrollContainer, duration])

  return { isScrolling: Boolean(isPlaying && !!duration) }
}
